const DRAG_OFFSET_X = 100;
const DRAG_OFFSET_Y = 100;
const ANIMATION_DELAY_MS = 2000;
const ANIMATION_DURATION_MS = 800;
const FADE_OUT_DURATION_MS = 600;
const AUTO_CLEANUP_MS = 5000;
const CARD_POLL_INTERVAL_MS = 300;
const CARD_POLL_MAX_ATTEMPTS = 50;

const CARD_SELECTOR = '[data-tour-target="lead-card"]';

function isCardReady(card: HTMLElement): boolean {
    if (card.textContent?.includes('Generating summary')) return false;
    if (card.querySelector('svg[class*="loading"], svg[class*="spin"]')) return false;

    return true;
}

function cleanup(clone: HTMLElement, card: HTMLElement, originalOpacity: string): void {
    clone.style.opacity = '0';
    clone.style.transition = `opacity ${FADE_OUT_DURATION_MS}ms ease`;
    card.style.opacity = originalOpacity;
    card.style.transition = `opacity ${FADE_OUT_DURATION_MS}ms ease`;
    setTimeout(() => {
        clone.remove();
        card.style.transition = '';
    }, FADE_OUT_DURATION_MS);
}

function animateCard(card: HTMLElement): () => void {
    const rect = card.getBoundingClientRect();

    const originalOpacity = card.style.opacity;
    card.style.opacity = '0.4';
    card.style.transition = `opacity ${ANIMATION_DURATION_MS}ms ease`;

    const clone = card.cloneNode(true) as HTMLElement;
    clone.removeAttribute('data-tour-target');

    Object.assign(clone.style, {
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        zIndex: '10000',
        pointerEvents: 'none',
        transform: 'rotate(0deg) translate(0px, 0px)',
        boxShadow: '0 2px 3px rgba(0, 0, 0, 0.3)',
        borderRadius: '3px',
        transition: `transform ${ANIMATION_DURATION_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
        opacity: '1',
    });

    document.body.appendChild(clone);

    let cleaned = false;
    const doCleanup = () => {
        if (cleaned) return;
        cleaned = true;
        window.clearTimeout(animationTimer);
        window.clearTimeout(autoCleanupTimer);
        cleanup(clone, card, originalOpacity);
    };

    const animationTimer = window.setTimeout(() => {
        clone.style.transform = `rotate(5deg) translate(${DRAG_OFFSET_X}px, ${DRAG_OFFSET_Y}px)`;
    }, ANIMATION_DELAY_MS);

    const autoCleanupTimer = window.setTimeout(doCleanup, AUTO_CLEANUP_MS);

    return doCleanup;
}

/**
 * Creates a visual simulation of dragging the first lead card on the board.
 * Polls for the card element and waits for its AI summary to finish loading,
 * then clones and animates it sliding down-right with a tilt. The clone
 * automatically fades out after 5 seconds regardless of tour state.
 */
export function simulateLeadCardDrag(): (() => void) | void {
    const card = document.querySelector<HTMLElement>(CARD_SELECTOR);
    if (card && isCardReady(card)) {
        return animateCard(card);
    }

    let attempts = 0;
    let animationCleanup: (() => void) | undefined;

    const pollTimer = window.setInterval(() => {
        attempts++;
        const found = document.querySelector<HTMLElement>(CARD_SELECTOR);
        if (found && isCardReady(found)) {
            window.clearInterval(pollTimer);
            animationCleanup = animateCard(found);
        } else if (attempts >= CARD_POLL_MAX_ATTEMPTS) {
            window.clearInterval(pollTimer);
        }
    }, CARD_POLL_INTERVAL_MS);

    return () => {
        window.clearInterval(pollTimer);
        animationCleanup?.();
    };
}
